// src/pages/SitesMap.js
import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import { Icon } from "leaflet";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import MapViewer from "../components/MapViewer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Couleurs de la charte graphique
const COLORS = {
  primary: '#0B3C5D', // Bleu Sécurité
  secondary: '#1E88C8', // Bleu Tech
  accent: '#4FC3F7', // Bleu Clair
  darkText: '#2E2E2E', // Gris Foncé
  lightBg: '#F5F7FA', // Gris Clair
  white: '#FFFFFF',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545'
};

// Icône personnalisée pour les sites
const createSiteIcon = (statut) => {
  return new Icon({
    iconUrl: statut === 1 
      ? 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png'
      : 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Icône pour le centre de la carte (position utilisateur)
const createCenterIcon = () => {
  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
        <circle cx="12" cy="12" r="10" fill="${COLORS.primary}" opacity="0.8"/>
        <circle cx="12" cy="12" r="3" fill="${COLORS.accent}"/>
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="${COLORS.white}" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `)}`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Position par défaut (Niamey, Niger)
const NIAMEY_CENTER = [13.5137, 2.1098];
const DEFAULT_ZOOM = 12;

const SitesMap = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [mapCenter, setMapCenter] = useState(NIAMEY_CENTER); // Par défaut Niamey
  const [selectedSite, setSelectedSite] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedSiteForModal, setSelectedSiteForModal] = useState(null);
  const [geolocationStatus, setGeolocationStatus] = useState("idle"); // idle, loading, success, denied
  const [userLocation, setUserLocation] = useState(null); // Position de l'utilisateur
  const [notification, setNotification] = useState(null);

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Fonction pour obtenir la géolocalisation de l'utilisateur
  const getUserGeolocation = useCallback(() => {
    setGeolocationStatus("loading");
    
    if (!navigator.geolocation) {
      setGeolocationStatus("denied");
      setError("La géolocalisation n'est pas supportée par votre navigateur");
      showNotification("Géolocalisation non supportée", "Votre navigateur ne supporte pas la géolocalisation.", "warning");
      return;
    }
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const userPos = {
          lat: latitude,
          lng: longitude
        };
        
        setUserLocation(userPos);
        setMapCenter([latitude, longitude]);
        setGeolocationStatus("success");
        
        showNotification("Position détectée", "La carte a été centrée sur votre position actuelle.", "success");
      },
      (error) => {
        console.error("Erreur de géolocalisation:", error);
        setGeolocationStatus("denied");
        
        let errorMessage = "Impossible d'obtenir votre position. ";
        let notificationType = "warning";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Vous avez refusé l'accès à la géolocalisation.";
            notificationType = "info";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "La position n'est pas disponible.";
            break;
          case error.TIMEOUT:
            errorMessage += "La requête a expiré.";
            break;
          default:
            errorMessage += "Erreur inconnue.";
        }
        
        // Si la géolocalisation est refusée, on centre sur Niamey
        setMapCenter(NIAMEY_CENTER);
        
        // Afficher une notification
        showNotification("Centré sur Niamey", errorMessage, notificationType);
      },
      options
    );
  }, []);

  // Fonction pour afficher une notification
  const showNotification = (title, message, type = "info") => {
    const notificationId = Date.now();
    setNotification({
      id: notificationId,
      title,
      message,
      type
    });
    
    // Supprimer la notification après 5 secondes
    setTimeout(() => {
      setNotification(prev => prev?.id === notificationId ? null : prev);
    }, 5000);
  };

  // Essayer d'obtenir la géolocalisation au chargement de la page
  useEffect(() => {
    if (userInfo) {
      getUserGeolocation();
    }
  }, [userInfo, getUserGeolocation]);

  // Charger tous les sites
  useEffect(() => {
    const fetchAllSites = async () => {
      if (!userInfo) return;
      
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/sites/list.php?limit=1000&supprimer=0`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success) {
          const sitesData = response.data.data || [];
          
          const sitesWithCoords = sitesData.filter(site => 
            site.latitude_site && site.longitude_site &&
            site.latitude_site !== 0 && site.longitude_site !== 0
          );
          
          setSites(sitesWithCoords);
          
          // Si on a des sites avec coordonnées et que l'utilisateur n'a pas de géolocalisation,
          // on centre la carte sur la moyenne des sites
          if (sitesWithCoords.length > 0 && !userLocation) {
            const avgLat = sitesWithCoords.reduce((sum, site) => sum + parseFloat(site.latitude_site), 0) / sitesWithCoords.length;
            const avgLng = sitesWithCoords.reduce((sum, site) => sum + parseFloat(site.longitude_site), 0) / sitesWithCoords.length;
            setMapCenter([avgLat, avgLng]);
          }
        } else {
          setError(response.data?.message || "Erreur de chargement");
        }
      } catch (err) {
        console.error("Erreur:", err);
        
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        } else if (!err.response) {
          setError("Serveur indisponible.");
        } else {
          setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (userInfo) {
      fetchAllSites();
    }
  }, [userInfo, navigate, userLocation]);

  // Fonction pour le bouton de recentrage
  const RecenterButton = () => {
    return (
      <div className="leaflet-top leaflet-right">
        <div className="leaflet-control leaflet-bar">
          <button
            onClick={getUserGeolocation}
            title="Centrer sur ma position"
            style={{
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.lightBg}`,
              color: geolocationStatus === "success" ? COLORS.success : COLORS.primary,
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.2rem',
              transition: 'all 0.3s ease',
              marginBottom: '4px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = COLORS.lightBg;
              e.target.style.borderColor = COLORS.secondary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.borderColor = COLORS.lightBg;
            }}
          >
            {geolocationStatus === "loading" ? (
              <i className="bi bi-hourglass"></i>
            ) : (
              <i className="bi bi-geo-alt"></i>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Calculer les statistiques
  const stats = {
    total: sites.length,
    active: sites.filter(s => s.statut_site === 1).length,
    inactive: sites.filter(s => s.statut_site === 0).length,
    noCoords: sites.filter(s => !s.latitude_site || !s.longitude_site || s.latitude_site === 0 || s.longitude_site === 0).length,
    uniqueClients: [...new Set(sites.map(s => s.id_client))].length
  };

  // Permission
  const canAccess = userInfo && (String(userInfo.profile).trim() === "2" || String(userInfo.profile).trim() === "3");

  // Fonction pour ouvrir la modal de visualisation de carte
  const openMapModal = (site) => {
    setSelectedSiteForModal(site);
    setShowMapModal(true);
  };

  if (loading || !userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
            Carte des sites
          </h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement de la carte...</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!canAccess) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
            Carte des sites
          </h1>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: `1px solid ${COLORS.lightBg}`
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: COLORS.lightBg,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${COLORS.primary}`
                }}>
                  <i className="bi bi-lock" style={{ fontSize: '2rem', color: COLORS.primary }}></i>
                </div>
              </div>
              <h5 className="mt-3" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif" }}>
                Accès restreint
              </h5>
              <p className="text-muted">Seuls les gestionnaires et contrôleurs peuvent accéder à cette page.</p>
              <button
                className="btn mt-3"
                onClick={() => navigate("/dashboard")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  padding: '0.5rem 2rem',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          Carte des sites
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.secondary }}>
                Tableau de bord
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/sites" style={{ color: COLORS.secondary }}>
                Sites
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
              Carte géographique
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            {/* Notification flottante */}
            {notification && (
              <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999,
                minWidth: '300px',
                maxWidth: '400px'
              }}>
                <div className={`alert alert-${notification.type} alert-dismissible fade show`} style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: 'none',
                  animation: 'slideInRight 0.3s ease-out'
                }}>
                  <div className="d-flex align-items-start">
                    <i className={`bi ${notification.type === 'success' ? 'bi-check-circle' : notification.type === 'warning' ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-2`} 
                       style={{ fontSize: '1.2rem', marginTop: '2px' }}></i>
                    <div className="flex-grow-1">
                      <h6 className="alert-heading mb-1">{notification.title}</h6>
                      <p className="mb-0 small">{notification.message}</p>
                    </div>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setNotification(null)}
                      style={{ fontSize: '0.75rem' }}
                    ></button>
                  </div>
                </div>
              </div>
            )}

            {/* En-tête avec titre et boutons */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
                  <i className="bi bi-map me-2" style={{ color: COLORS.secondary }}></i>
                  Visualisation géographique des sites
                </h5>
                <p className="text-muted mb-0">
                  Localisez et gérez vos sites de sécurité sur une carte interactive
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <button
                  onClick={getUserGeolocation}
                  className="btn"
                  disabled={geolocationStatus === "loading"}
                  style={{
                    color: geolocationStatus === "success" ? COLORS.success : COLORS.primary,
                    borderColor: geolocationStatus === "success" ? COLORS.success : COLORS.primary,
                    backgroundColor: geolocationStatus === "success" ? '#e8f5e9' : 'transparent',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (geolocationStatus !== "loading") {
                      if (geolocationStatus !== "success") {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (geolocationStatus !== "success") {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }
                  }}
                >
                  {geolocationStatus === "loading" ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Détection...
                    </>
                  ) : geolocationStatus === "success" ? (
                    <>
                      <i className="bi bi-check-circle me-1"></i>
                      Position détectée
                    </>
                  ) : (
                    <>
                      <i className="bi bi-geo-alt me-1"></i>
                      Ma position
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => navigate("/sites")}
                  className="btn"
                  style={{
                    color: COLORS.darkText,
                    borderColor: COLORS.lightBg,
                    backgroundColor: COLORS.white,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${COLORS.lightBg}`
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.lightBg;
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.white;
                    e.target.style.borderColor = COLORS.lightBg;
                  }}
                >
                  <i className="bi bi-list me-1"></i>
                  Vue liste
                </button>
                
                <button
                  onClick={() => window.location.reload()}
                  className="btn"
                  disabled={loading}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = COLORS.white;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Actualiser
                </button>
              </div>
            </div>

            {error && (
              <div className="alert" style={{
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                borderColor: COLORS.danger,
                color: COLORS.danger,
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Cartes de statistiques */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.primary}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(11, 60, 93, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.primary}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-geo-alt" style={{ fontSize: '1.5rem', color: COLORS.primary }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Sites localisés
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 700 }}>
                          {stats.total}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.success}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(40, 167, 69, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.success}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-check-circle" style={{ fontSize: '1.5rem', color: COLORS.success }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Sites actifs
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.success, fontFamily: "'Inter', sans-serif", fontWeight: 700 }}>
                          {stats.active}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.warning}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.warning}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.5rem', color: COLORS.warning }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Sans coordonnées
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.warning, fontFamily: "'Inter', sans-serif", fontWeight: 700 }}>
                          {stats.noCoords}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.secondary}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(30, 136, 200, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.secondary}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-building" style={{ fontSize: '1.5rem', color: COLORS.secondary }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Clients différents
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.secondary, fontFamily: "'Inter', sans-serif", fontWeight: 700 }}>
                          {stats.uniqueClients}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Carte Leaflet */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1px solid ${COLORS.lightBg}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <div className="card-body p-0">
                <div style={{ height: '600px', position: 'relative' }}>
                  {sites.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center h-100" style={{ backgroundColor: COLORS.lightBg }}>
                      <div className="text-center p-5">
                        <div style={{
                          width: '80px',
                          height: '80px',
                          backgroundColor: `${COLORS.primary}15`,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '1rem'
                        }}>
                          <i className="bi bi-map" style={{ fontSize: '2rem', color: COLORS.primary }}></i>
                        </div>
                        <h5 className="mt-3" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif" }}>
                          Aucun site avec coordonnées GPS
                        </h5>
                        <p className="text-muted mb-4">
                          Ajoutez des coordonnées GPS aux sites pour les visualiser sur la carte.
                        </p>
                        <button
                          onClick={() => navigate("/sites")}
                          className="btn"
                          style={{
                            backgroundColor: COLORS.primary,
                            color: COLORS.white,
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 500,
                            padding: '0.5rem 2rem',
                            borderRadius: '8px',
                            border: 'none',
                            transition: 'background-color 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = COLORS.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = COLORS.primary;
                          }}
                        >
                          <i className="bi bi-list me-1"></i>
                          Voir la liste des sites
                        </button>
                      </div>
                    </div>
                  ) : (
                    <MapContainer
                      center={mapCenter}
                      zoom={DEFAULT_ZOOM}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={true}
                      attributionControl={false}
                      whenCreated={(map) => {
                        setTimeout(() => {
                          map.invalidateSize();
                        }, 100);
                      }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      
                      {/* Bouton de recentrage */}
                      <RecenterButton />
                      
                      {/* Marqueur pour la position de l'utilisateur */}
                      {userLocation && (
                        <Marker position={[userLocation.lat, userLocation.lng]} icon={createCenterIcon()}>
                          <Popup>
                            <div style={{ padding: '5px', minWidth: '200px' }}>
                              <strong>Votre position actuelle</strong><br/>
                              <small>
                                <i className="bi bi-geo-alt me-1"></i>
                                {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                              </small>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      
                      {/* Marqueurs pour chaque site */}
                      {sites.map(site => (
                        <Marker
                          key={site.id_site}
                          position={[parseFloat(site.latitude_site), parseFloat(site.longitude_site)]}
                          icon={createSiteIcon(site.statut_site)}
                          eventHandlers={{
                            click: () => setSelectedSite(site),
                            mouseover: (e) => {
                              e.target.openPopup();
                            }
                          }}
                        >
                          <Popup>
                            <div style={{ minWidth: '280px', padding: '10px' }}>
                              <div className="d-flex align-items-center mb-3">
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: site.statut_site === 1 ? `${COLORS.success}20` : `${COLORS.danger}20`,
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '10px'
                                }}>
                                  <i className={`bi bi-building ${site.statut_site === 1 ? 'text-success' : 'text-secondary'}`}></i>
                                </div>
                                <div>
                                  <h6 className="mb-0" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
                                    {site.nom_site}
                                  </h6>
                                  <div className="d-flex gap-2 mt-1">
                                    <span className="badge" style={{ 
                                      backgroundColor: site.statut_site === 1 ? COLORS.success : '#6c757d',
                                      color: COLORS.white,
                                      fontSize: '0.75rem',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '4px'
                                    }}>
                                      {site.statut_site === 1 ? 'Actif' : 'Inactif'}
                                    </span>
                                    <span className="badge" style={{ 
                                      backgroundColor: `${COLORS.secondary}20`,
                                      color: COLORS.secondary,
                                      fontSize: '0.75rem',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '4px'
                                    }}>
                                      ID: {site.id_site}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mb-3">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="bi bi-person me-2" style={{ color: COLORS.secondary, width: '16px' }}></i>
                                  <div>
                                    <small style={{ color: COLORS.darkText, opacity: 0.7 }}>Client</small>
                                    <div style={{ color: COLORS.darkText, fontWeight: 500 }}>
                                      {site.nom_client || "Non spécifié"}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center mb-2">
                                  <i className="bi bi-geo-alt me-2" style={{ color: COLORS.secondary, width: '16px' }}></i>
                                  <div>
                                    <small style={{ color: COLORS.darkText, opacity: 0.7 }}>Adresse</small>
                                    <div style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                                      {site.addresse_site || "Non spécifiée"}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-geo me-2" style={{ color: COLORS.secondary, width: '16px' }}></i>
                                  <div>
                                    <small style={{ color: COLORS.darkText, opacity: 0.7 }}>Coordonnées</small>
                                    <div style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                                      {parseFloat(site.latitude_site).toFixed(6)}, {parseFloat(site.longitude_site).toFixed(6)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="d-grid gap-2">
                                <button
                                  onClick={() => navigate(`/sites/details/${site.id_site}`)}
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: COLORS.primary,
                                    color: COLORS.white,
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '0.375rem',
                                    fontFamily: "'Inter', sans-serif",
                                    fontWeight: 500,
                                    transition: 'background-color 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = COLORS.secondary;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-eye me-1"></i>
                                  Voir détails
                                </button>
                                <button
                                  onClick={() => openMapModal(site)}
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: COLORS.primary,
                                    border: `1px solid ${COLORS.primary}`,
                                    borderRadius: '6px',
                                    padding: '0.375rem',
                                    fontFamily: "'Inter', sans-serif",
                                    fontWeight: 500,
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = `${COLORS.primary}10`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <i className="bi bi-map me-1"></i>
                                  Visualiser sur carte
                                </button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                      
                      {/* Cercle autour du site sélectionné */}
                      {selectedSite && (
                        <Circle
                          center={[parseFloat(selectedSite.latitude_site), parseFloat(selectedSite.longitude_site)]}
                          radius={500}
                          pathOptions={{ 
                            color: COLORS.primary, 
                            fillColor: COLORS.primary,
                            fillOpacity: 0.1,
                            weight: 2
                          }}
                        />
                      )}
                    </MapContainer>
                  )}
                </div>
                
                {/* Légende */}
                <div className="p-3 border-top" style={{ backgroundColor: COLORS.lightBg }}>
                  <h6 className="mb-3" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Légende
                  </h6>
                  <div className="d-flex flex-wrap gap-4">
                    <div className="d-flex align-items-center">
                      <img 
                        src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png" 
                        alt="Actif" 
                        style={{ width: '20px', height: '33px', marginRight: '8px' }}
                      />
                      <small style={{ color: COLORS.darkText }}>Site actif</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <img 
                        src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png" 
                        alt="Inactif" 
                        style={{ width: '20px', height: '33px', marginRight: '8px' }}
                      />
                      <small style={{ color: COLORS.darkText }}>Site inactif</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '8px'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" fill={COLORS.primary} opacity="0.8"/>
                          <circle cx="12" cy="12" r="3" fill={COLORS.accent}/>
                          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke={COLORS.white} strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <small style={{ color: COLORS.darkText }}>Votre position</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: COLORS.primary,
                        opacity: 0.1,
                        marginRight: '8px',
                        border: `2px solid ${COLORS.primary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: COLORS.primary
                        }}></div>
                      </div>
                      <small style={{ color: COLORS.darkText }}>Rayon de 500m</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des sites sans coordonnées */}
            {stats.noCoords > 0 && (
              <div className="card" style={{ 
                borderRadius: '12px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: `${COLORS.warning}20`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}>
                      <i className="bi bi-exclamation-triangle" style={{ color: COLORS.warning }}></i>
                    </div>
                    <div>
                      <h6 className="card-title mb-0" style={{ color: COLORS.primary, fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
                        Sites sans coordonnées GPS
                      </h6>
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        {stats.noCoords} site(s) nécessite(nt) des coordonnées géographiques
                      </p>
                    </div>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="table table-hover" style={{ borderColor: COLORS.lightBg }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.lightBg }}>
                          <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.primary}` }}>
                            Site
                          </th>
                          <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.primary}` }}>
                            Client
                          </th>
                          <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.primary}` }}>
                            Statut
                          </th>
                          <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.primary}` }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sites
                          .filter(site => !site.latitude_site || !site.longitude_site || site.latitude_site === 0 || site.longitude_site === 0)
                          .map(site => (
                            <tr key={site.id_site} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                              <td style={{ color: COLORS.darkText, verticalAlign: 'middle' }}>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-building me-2" style={{ color: COLORS.secondary }}></i>
                                  {site.nom_site}
                                </div>
                              </td>
                              <td style={{ color: COLORS.darkText, verticalAlign: 'middle' }}>
                                {site.nom_client || '-'}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                <span className="badge" style={{ 
                                  backgroundColor: site.statut_site === 1 ? `${COLORS.success}20` : '#e9ecef',
                                  color: site.statut_site === 1 ? COLORS.success : COLORS.darkText,
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}>
                                  {site.statut_site === 1 ? 'Actif' : 'Inactif'}
                                </span>
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                <button
                                  onClick={() => navigate(`/sites/edit/${site.id_site}`)}
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: COLORS.primary,
                                    border: `1px solid ${COLORS.primary}`,
                                    borderRadius: '6px',
                                    padding: '0.25rem 0.75rem',
                                    fontFamily: "'Inter', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = `${COLORS.primary}10`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Ajouter coordonnées
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modal de visualisation de carte */}
      {selectedSiteForModal && (
        <MapViewer
          show={showMapModal}
          onHide={() => {
            setShowMapModal(false);
            setSelectedSiteForModal(null);
          }}
          latitude={selectedSiteForModal.latitude_site}
          longitude={selectedSiteForModal.longitude_site}
          siteName={selectedSiteForModal.nom_site}
          siteAddress={selectedSiteForModal.addresse_site}
          siteStatus={selectedSiteForModal.statut_site}
        />
      )}

      {/* CSS personnalisé */}
      <style jsx="true">{`
        /* Supprimer complètement l'attribution Leaflet */
        .leaflet-control-attribution {
          display: none !important;
        }
        
        /* Amélioration de la carte */
        .leaflet-container {
          font-family: 'Roboto', sans-serif;
          font-size: 14px;
        }
        
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          border: 1px solid ${COLORS.lightBg};
        }
        
        .leaflet-popup-content {
          margin: 0;
          line-height: 1.5;
        }
        
        .leaflet-popup-tip {
          background: ${COLORS.white};
          box-shadow: 0 3px 14px rgba(0,0,0,0.4);
        }
        
        .leaflet-marker-icon {
          transition: transform 0.2s ease;
        }
        
        .leaflet-marker-icon:hover {
          transform: scale(1.1);
        }
        
        /* Style pour les contrôles de la carte */
        .leaflet-control-zoom a {
          background-color: ${COLORS.white} !important;
          border-color: ${COLORS.lightBg} !important;
          color: ${COLORS.primary} !important;
          border-radius: 6px !important;
          margin: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        
        .leaflet-control-zoom a:hover {
          background-color: ${COLORS.lightBg} !important;
          border-color: ${COLORS.secondary} !important;
        }
        
        /* Style pour le cercle de sélection */
        .leaflet-overlay-pane path {
          stroke-width: 2;
        }
        
        /* Animation pour les notifications */
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SitesMap;